import { useCallback, useState } from "react";
import { useAui, useAuiState } from "@assistant-ui/react";
import { Loader2, RefreshCw } from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { truncateCopilotThreadMessages } from "../../lib/copilotApi";

/**
 * Action-bar "regenerate" button: truncates the persisted transcript from the
 * clicked assistant message, resets the local thread to the kept prefix, and
 * re-runs from the triggering user message (marked as a regenerate so the
 * runtime adapter does not re-persist it).
 */
export function ReloadResponseButton() {
  const aui = useAui();
  const activeThreadId = useAppSelector((s) => s.copilotReducer.activeThreadId);
  const [isReloading, setIsReloading] = useState(false);

  const messageId = useAuiState((s) => s.message.id);
  const messageIndex = useAuiState((s) => s.message.index);
  const messageParentId = useAuiState((s) => s.message.parentId);
  const messageRole = useAuiState((s) => s.message.role);
  const threadDisabled = useAuiState((s) => s.thread.isDisabled);
  const threadRunning = useAuiState((s) => s.thread.isRunning);
  const threadMessages = useAuiState((s) => s.thread.messages);

  const disabled =
    isReloading ||
    threadRunning ||
    threadDisabled ||
    messageRole !== "assistant";

  const handleReload = useCallback(async () => {
    if (disabled) return;

    setIsReloading(true);
    try {
      if (activeThreadId) {
        await truncateCopilotThreadMessages(activeThreadId, messageIndex);
      }

      const keptMessages = threadMessages.slice(0, messageIndex);
      aui.thread().reset(
        keptMessages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
          ...(m.role === "user" ? { attachments: m.attachments } : {}),
          ...(m.role === "assistant" ? { status: m.status } : {}),
          ...(m.metadata ? { metadata: m.metadata } : {}),
        })),
      );

      const parentIdForRun = messageParentId ?? keptMessages.at(-1)?.id ?? null;
      aui.thread().startRun({
        parentId: parentIdForRun,
        sourceId: messageId,
        // Marks this as a regenerate so the runtime adapter does not re-persist
        // the (already stored) triggering user message.
        runConfig: { custom: { regenerate: true } },
      });
    } catch (err) {
      console.error("Failed to reload assistant response", err);
    } finally {
      setIsReloading(false);
    }
  }, [
    activeThreadId,
    aui,
    disabled,
    messageId,
    messageIndex,
    messageParentId,
    threadMessages,
  ]);

  return (
    <button
      type="button"
      onClick={() => void handleReload()}
      disabled={disabled}
      className="flex items-center gap-1 rounded-md px-2 py-1 text-neutral-400 transition-colors hover:text-neutral-300 disabled:cursor-not-allowed disabled:opacity-40"
      title="Regenerate response"
      aria-label="Regenerate response"
    >
      {isReloading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
    </button>
  );
}
